<template>
  <wui-modal
    v-click-outside="scheduleClickOutside"
    :title="event ? $t('common.editEvent') : t('chat.scheduleCreateFormTitle')"
    :size="40"
    class="m-2 !w-[520px] !max-w-[calc(100vw-16px)] !max-h-[calc(100vh-16px)] phone:!w-[360px] bg-bg-base-bg3!"
    @close="cancel"
    @pointerdown="onPointerDown"
  >
    <div>
      <div class="p-4">
        <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1">
          {{ t('common.name') }}
        </p>
        <wui-input
          v-model="eventName"
          :error="isNameTooLong"
          :supporting-text="isNameTooLong ? $t('common.theNameIsTooLong') : ''"
        />
        <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 mt-3 ml-1">
          {{ t('common.description') }}
        </p>
        <wui-textarea
          v-model="eventDescription!"
          class="rounded-12 max-h-[130px] min-h-[50px]"
          :maxlength="API_FIELD_MAX_LENGTH"
          :placeholder="$t('common.maxLength', { maxLength: API_FIELD_MAX_LENGTH })"
        />
      </div>
      <div class="p-4">
        <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1">
          {{ t('chat.scheduleDateAndTime') }}
        </p>
        <schedule-date-time-input
          v-model="dateTimeSettings"
          v-model:period-mode="periodMode"
          v-model:is-time-invalid="isTimeInvalid"
          :is-edit="!!event"
          class="rounded-12 m-auto"
        />
      </div>

      <div class="p-4">
        <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1">
          {{ t('common.participants') }}
        </p>
        <participants-select
          v-model:model-value="participants"
          v-model:text="string"
          :items="items"
          :all-items="allItems"
          :fields-search-by="['name', 'internalNumber', 'mobilePhone', 'email']"
          id-key="guid"
          multiple
          :on-end-reached-cb="fetchNext"
          :chip-validator-fn="participantSelectChipValidator"
        >
          <template #suggestion="{ suggestion }">
            <participant-item
              :contact="suggestion.value"
            />
          </template>
        </participants-select>
      </div>
      <div class="p-4">
        <div
          v-if="!eventId && isChatMessagingUiEnabled"
          data-test="schedule-link-to-chat"
        >
          <p class="text-label-r-13-18 mb-1 text-fg-base-fg14 ml-1">
            {{ t('calendar.linkToChat') }}
          </p>
          <combobox
            v-model="selectedRoomPServed!"
            class="mt-0!"
            :items="groups"
            :is-multiple="false"
            :allow-custom-value="false"
            :placeholder="$t('calendar.selectChat')"
            :label="false"
            with-clear
          />
        </div>
        <div class="mt-3">
          <wui-input-switch
            v-model="isGuestLinkAccessEnabled"
            data-test="schedule-guest-link-access"
            small
            :label="$t('chat.allowLinkAccess')"
            :description="guestLinkAccessHint"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="wui-modal__spacer" />
      <wui-btn
        v-if="hasSchedule"
        :size="40"
        variant="negative"
        state="alpha"
        :loading="isLoading"
        @click="askToDeleteScheduledEvent"
      >
        {{ t('common.remove') }}
      </wui-btn>
      <wui-btn
        :size="40"
        :disabled="isNotValid || isLoading"
        variant="brand"
        state="filled"
        auto-loading
        @click="onEnable"
      >
        {{ hasSchedule ? t('common.save') : t('common.create') }}
      </wui-btn>
    </template>
  </wui-modal>
</template>
<script lang="ts" setup>
import {
  WuiBtn,
  WuiInput,
  WuiInputSwitch,
  WuiModal,
  closeDialogKey,
  ClickOutside as vClickOutside,
  safeInject,
  SelectItem,
  useDialog,
  WuiTextarea,
  WuiCombobox,
} from '@wui/common-library'
import { ConferenceType, ConferenceMemberRole, ContactState } from '@wui/im'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { IcsAttendee, IcsEvent } from 'ts-ics'
import { v4 as uuidV4 } from 'uuid'
import { Component, computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'


import { ConfirmEventCancelDialog } from '@/features/confirm-event-cancel'
import {
  generateIcsEventRules,
  parseIcsEventRules,
  formatScheduleNotificationText,
  ScheduleDateTimeInput,
  ScheduleDateTimeInputPayload,
  Repeat,
  calendarDateToStartOfDay,
  getDefaultScheduleTimeRange,
} from '@/features/schedule'

import { ParticipantItem, useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'
import {
  decodeIcsMultilineText,
  extractCalendarParticipantKey,
  getCalendarEmailValue,
  getDefaultEvent,
  getScheduleContactPhone,
  getScheduleEventDisplayName,
  hasValidCalendarEmail,
  isGuestScheduleAttendee,
  isGuestScheduleContact,
  isOrganizerAttendee,
  isOrganizerContact,
  isOrganizerParticipant,
  normalizeCalendarEmail,
  PeriodMode,
  SCHEDULE_EVENT_ID_SEPARATOR,
  toScheduleOrganizerIdentity,
  useCalendarAttendeeContacts,
  useRoomScheduleStore,
  withOrganizerAsAcceptedAttendee,
} from '@/entities/schedule'
import { useRoom } from '@/entities/use-room'

import { getParticipantsSelectSuggestionsElements, ParticipantsSelect, isEmail } from '@/shared/components/participants-select'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getProfileTimezone } from '@/shared/composables/timezone'
import { usePreventClickOutsideOnDrag } from '@/shared/composables/use-prevent-click-outside-on-drag'
import { useUserFeatures } from '@/shared/composables/user-features'
import { Contact } from '@/shared/types/api'
import { API_FIELD_MAX_LENGTH , pathNames } from '@/shared/utils/constants'
import { fromPServedUser } from '@/shared/utils/uri'
import { getAbsoluteURL, getInviteLoginAbsoluteURL } from '@/shared/utils/url'

import { useScheduleParticipantsSelect } from '../lib'

const props = (defineProps<{
  date?: Date | string,
  eventId?: string
  scheduleEventName?: string
  pServed?: string
  event?: IcsEvent
}>())

const { t } = useLocalization()
const { isChatMessagingUiEnabled } = useUserFeatures()
const { createConference, enableGuestAccess } = useRoom()
const { findByPServed: findRoom, updateRoom } = useStoreRooms()
const scheduleStore = useRoomScheduleStore()
const { isLoading } = storeToRefs(scheduleStore)
const { enable, update } = scheduleStore
const { currentUser } = useCurrentUser()
const organizerIdentity = computed(() => toScheduleOrganizerIdentity(currentUser.value))
const contactsStore = useStoreContacts()
const { findByInternalNumber, findByPServed, findByGuid, findById, findContactByMobilePhone } = contactsStore
const { allContacts } = storeToRefs(contactsStore)
const { buildAttendee, findContactByEmail, resolveAttendee } = useCalendarAttendeeContacts()
const { freeAndWhereIAmModeratorRooms } = storeToRefs(useStoreRooms())
const { showDialog } = useDialog()

const pServed = computed(() => props.eventId?.split(SCHEDULE_EVENT_ID_SEPARATOR).at(0) ?? props.pServed)
const eventUid = computed(() => props.eventId?.split(SCHEDULE_EVENT_ID_SEPARATOR).at(1))
const room = computed(() => pServed.value ? findRoom(pServed.value) : undefined)
const hasSchedule = computed(() => props.event)
const groups = computed<SelectItem<string | number>[]>(() => freeAndWhereIAmModeratorRooms.value.filter(room => room.isGroup).map(group => ({
  label: group.name,
  value: group.pServedUser,
})))

const Combobox = WuiCombobox as Component

const eventName = ref(decodeIcsMultilineText(props.event?.summary) ?? '')
const eventDescription = ref(decodeIcsMultilineText(props.event?.description) ?? '')

const isNameTooLong = computed (() => {
  return eventName.value.length > API_FIELD_MAX_LENGTH
})

const getGuidByPServed = (pServed: string) => {
  if (pServed === currentUser.value?.imLogin) {
    return currentUser.value.guid
  }

  return findByPServed(pServed)?.guid
}

const convertAttendeeToParticipantId = (attendee: IcsAttendee): string | null => {
  const contact = resolveAttendee(attendee)
  if (currentUser.value && contact) {
    if (
      contact.guid === currentUser.value.guid
      || contact.imLogin === currentUser.value.imLogin
      || contact.internalNumber === currentUser.value.internalNumber
    ) {
      return currentUser.value.guid ?? null
    }
  }

  if (contact?.guid) {
    return contact.guid
  }

  if (isOrganizerAttendee(attendee, props.event?.organizer, organizerIdentity.value)) {
    return currentUser.value?.guid ?? (getCalendarEmailValue(attendee.email) || null)
  }

  if (hasValidCalendarEmail(attendee.email)) {
    return getCalendarEmailValue(attendee.email)
  }

  return extractCalendarParticipantKey(attendee.email) || attendee.name || null
}

const collectParticipantIdentityKeys = (id: string): string[] => {
  const keys = new Set<string>([id.trim().toLowerCase()])

  const addContactKeys = (contact?: Pick<Contact, 'guid' | 'email' | 'internalNumber' | 'mobilePhone'> | null) => {
    if (!contact) {
      return
    }
    if (contact.guid) {
      keys.add(contact.guid.toLowerCase())
    }
    if (contact.internalNumber) {
      keys.add(contact.internalNumber.toLowerCase())
    }
    if (contact.mobilePhone) {
      keys.add(contact.mobilePhone.toLowerCase())
    }
  }

  if (currentUser.value?.guid === id) {
    addContactKeys(currentUser.value as Contact)
    return [...keys]
  }

  addContactKeys(findByGuid(id))

  if (isEmail(id)) {
    keys.add(normalizeCalendarEmail(id))
    addContactKeys(findContactByEmail(id))
  } else {
    addContactKeys(findByInternalNumber(id))
    addContactKeys(findContactByMobilePhone(id))
    addContactKeys(findById(id))
  }

  return [...keys]
}

const dedupeParticipantIds = (ids: string[]): string[] => {
  const result: string[] = []
  const seen = new Set<string>()

  ids.forEach((id) => {
    const identityKeys = collectParticipantIdentityKeys(id)
    if (identityKeys.some((key) => seen.has(key))) {
      return
    }
    identityKeys.forEach((key) => seen.add(key))
    result.push(id)
  })

  return result
}

const buildParticipantsFromAttendees = (attendees: IcsAttendee[]): string[] => {
  const eventParticipants = attendees
    .filter((attendee) => !isGuestScheduleAttendee(attendee))
    .map(convertAttendeeToParticipantId)
    .filter((id): id is string => Boolean(id))

  return dedupeParticipantIds(eventParticipants)
}

const getRoomParticipantIds = (contacts: ContactState[]): string[] =>
  dedupeParticipantIds(
    contacts
      .filter((contact) => !isGuestScheduleContact(contact))
      .map(({ id }) => getGuidByPServed(id) ?? id),
  )

const isCurrentUserParticipantId = (id: string): boolean => {
  if (!currentUser.value || !id) {
    return false
  }

  if (id === currentUser.value.guid) {
    return true
  }

  if (isOrganizerParticipant(id, organizerIdentity.value)) {
    return true
  }

  const contact = findByGuid(id) ?? findByInternalNumber(id) ?? findContactByMobilePhone(id)
  if (contact && isOrganizerContact(contact, organizerIdentity.value)) {
    return true
  }

  const currentUserKeys = new Set(collectParticipantIdentityKeys(currentUser.value.guid))
  return collectParticipantIdentityKeys(id).some((key) => currentUserKeys.has(key))
}

const participants = ref<Array<string>>((() => {
  if (props.event?.attendees) {
    return buildParticipantsFromAttendees(props.event.attendees)
  }
  return room.value?.contacts
    ? getRoomParticipantIds(room.value.contacts)
    : (currentUser.value?.guid ? [currentUser.value.guid] : [])
})())
const schedule = ref<IcsEvent>(
  props.event ? { ...props.event } : getDefaultEvent(
    props.date
      ? (typeof props.date === 'string' ? dayjs.tz(props.date).toDate() : props.date)
      : new Date(),
    eventName.value,
    eventUid.value,
  ),
)
const selectedRoomPServed = ref(props.pServed ?? '')

const selectedRoom = computed(() => findRoom(selectedRoomPServed.value))
const { string, fetchNext, items, allItems } = useScheduleParticipantsSelect(selectedRoom)

const resolveParticipantSelection = (id: string): string => {
  if (id === currentUser.value?.guid) {
    return currentUser.value.internalNumber
  }

  const contact = findByGuid(id)
    ?? findById(id)
    ?? findByPServed(id)
    ?? findByInternalNumber(id)
    ?? findContactByMobilePhone(id)
  if (contact) {
    const phone = getScheduleContactPhone(contact)
    if (phone) {
      return phone
    }

    if (hasValidCalendarEmail(contact.email)) {
      return getCalendarEmailValue(contact.email)
    }
  }

  if (isEmail(id)) {
    return id
  }

  return extractCalendarParticipantKey(id) || id
}

const buildAttendeesFromParticipants = (): IcsAttendee[] => {
  const attendees: IcsAttendee[] = []
  participants.value.forEach((id) => {
    const attendee = buildAttendee(id) ?? buildAttendee(resolveParticipantSelection(id))
    if (attendee) {
      attendees.push(attendee)
    }
  })

  return attendees
}

const isGuestLinkAccessEnabled = ref(Boolean(room.value?.token || selectedRoom.value?.token))
const guestLinkAccessHint = computed(() => isGuestLinkAccessEnabled.value
  ? t('calendar.guestLinkHintEnabled')
  : t('calendar.guestLinkHintDisabled'))

// Инициализация periodMode и dateTimeSettings из события, если оно есть
const initializeFromEvent = (): { periodMode: PeriodMode, dateTimeSettings: ScheduleDateTimeInputPayload } => {
  if (props.event) {
    const { periodMode: parsedPeriodMode, payload: parsedPayload } = parseIcsEventRules(props.event)
    return {
      periodMode: parsedPeriodMode,
      dateTimeSettings: parsedPayload,
    }
  }
  const profileTimezone = getProfileTimezone()
  return {
    periodMode: PeriodMode.ONCE,
    dateTimeSettings: {
      startDate: props.date
        ? calendarDateToStartOfDay(props.date)
        : dayjs().tz(profileTimezone).startOf('day').valueOf(),
      selectedDays: [],
      selectedWeekdays: [],
      selectedTime: getDefaultScheduleTimeRange(dayjs().tz(profileTimezone)),
      repeatOptions: {
        repeat: 'always' as Repeat,
        repeatDays: 1,
      },
      summary: '',
    },
  }
}

const { periodMode: initialPeriodMode, dateTimeSettings: initialDateTimeSettings } = initializeFromEvent()

const periodMode = ref(initialPeriodMode)
const dateTimeSettings = ref<ScheduleDateTimeInputPayload>(initialDateTimeSettings)
const isTimeInvalid = ref(false)

const isNotValid = computed<boolean>(() => {
  return participants.value.length === 0 || isNameTooLong.value || isTimeInvalid.value
})

const closeDialog = safeInject(closeDialogKey)
const { onPointerDown, createClickOutsideHandler } = usePreventClickOutsideOnDrag()
const scheduleClickOutside = {
  ...createClickOutsideHandler(closeDialog),
  include: getParticipantsSelectSuggestionsElements,
}

const cancel = () => {
  closeDialog(true)
}

watch([periodMode, dateTimeSettings, eventName, eventDescription, participants], () => {
  schedule.value = {
    ...generateIcsEventRules(periodMode.value, dateTimeSettings.value),
    attendees: buildAttendeesFromParticipants(),
    description: eventDescription.value!,
    summary: eventName.value,
    stamp: {
      date: new Date(),
      type: 'DATE-TIME' as const,
    },
    url: getAbsoluteURL(location.pathname),
    uid: schedule.value.uid ? schedule.value.uid : uuidV4(),
    ...(schedule.value.location ? { location: schedule.value.location } : {}),
    ...(typeof schedule.value.sequence === 'number' ? { sequence: schedule.value.sequence } : {}),
  }
}, { deep: true, immediate: true })

const router = useRouter()

const normalizeEnableError = (e: unknown): Error => {
  if (e instanceof Error) {
    return e
  }
  if (typeof e === 'string') {
    return new Error(e)
  }
  const maybeMessage = (e as { message?: unknown } | null)?.message
  if (typeof maybeMessage === 'string' && maybeMessage.trim().length) {
    return new Error(maybeMessage)
  }
  return new Error(t('common.unknownError'))
}

const getInternalConferenceLocation = (pServed: string, roomId?: string): string => {
  const path = router.resolve({
    name: pathNames.PREPARE_TO_CALL,
    params: {
      roomId: roomId ?? fromPServedUser(pServed),
    },
  }).href

  return getAbsoluteURL(`/${path}`)
}

const resolveConferenceLocation = async (pServed: string, roomId?: string): Promise<string> => {
  if (!isGuestLinkAccessEnabled.value) {
    return getInternalConferenceLocation(pServed, roomId)
  }

  const conferenceRoom = findRoom(pServed)
  let token = conferenceRoom?.token

  if (!token) {
    const { item } = await enableGuestAccess(pServed)
    token = item.token
    if (conferenceRoom && token) {
      updateRoom(conferenceRoom.id, { token })
    }
  }

  if (!token) {
    throw new Error(t('chat.failedToEnableGuestAccess'))
  }

  return getInviteLoginAbsoluteURL({
    roomPServed: pServed,
    roomToken: token,
    prepareToCall: true,
  })
}

const onEnable = async () => {
  try {
    let pServed = selectedRoomPServed.value

    const participantsNumbersAndEmails = participants.value.map(resolveParticipantSelection)

    if (!pServed && !hasSchedule.value) {
      const response = await createConference({
        mode: ConferenceType.FREE_FOR_ALL,
        name: getScheduleEventDisplayName(eventName.value, formatScheduleNotificationText(schedule.value)),
        subscribers: [
          ...participantsNumbersAndEmails
            .filter(number => !isEmail(number) && number !== currentUser.value?.internalNumber)
            .map(number => ({
              number,
              role: findByInternalNumber(number) ? 'User' : 'External' as ConferenceMemberRole,
            })),
        ],
      })
      pServed = response.pServed
    }

    const foundRoom = findRoom(pServed)
    if (foundRoom?.mode === ConferenceType.MODERATED) {
      const myRole = foundRoom.contacts?.find(contact => contact.id === currentUser.value?.imLogin)?.role
      const canManageSchedule = myRole === 'Moderator' || myRole === 'Owner'
      if (!canManageSchedule) {
        throw new Error(t('common.youCantCreateEventInModerateRoom'))
      }
    }

    const isCreatorParticipating = participantsNumbersAndEmails.some(isCurrentUserParticipantId)
    schedule.value.attendees = buildAttendeesFromParticipants()

    const location = await resolveConferenceLocation(pServed, foundRoom?.id)
    const summary = eventName.value.trim() || t('common.notSet')
    const eventToSave = withOrganizerAsAcceptedAttendee(
      {
        ...schedule.value,
        summary,
        location,
        url: location,
      },
      organizerIdentity.value,
      { includeCreatorAsAttendee: isCreatorParticipating },
    )
    schedule.value = eventToSave

    if (hasSchedule.value) {
      await update(pServed, eventToSave)
    } else {
      await enable(pServed, eventToSave)
    }

    closeDialog({
      pServed,
      participants: participantsNumbersAndEmails,
      uid: schedule.value.uid,
    })
  } catch (error) {
    closeDialog({
      error: normalizeEnableError(error),
    })
  }
}

const askToDeleteScheduledEvent = () => {
  if (!props.event) {
    return
  }

  showDialog(ConfirmEventCancelDialog, {
    scheduledEventName: getScheduleEventDisplayName(props.event.summary, t('common.withoutTitle')),
    scheduledEventUid: props.event.uid,
    pServed: props.pServed!,
  })
}

const participantSelectChipValidator = (id: string): boolean => currentUser.value?.guid === id

watch(eventName, (newValue) => {
  schedule.value.summary = newValue
})

// Только смена выбранного чата (не обновление объекта room в store) — иначе состав
// участников из ATTENDEE затирается всеми members комнаты при редактировании.
watch(selectedRoomPServed, (nextPServed) => {
  const nextRoom = findRoom(nextPServed)
  if (!nextRoom) {
    return
  }

  isGuestLinkAccessEnabled.value = Boolean(nextRoom.token)

  if (hasSchedule.value) {
    return
  }

  participants.value = nextRoom.contacts
    ? getRoomParticipantIds(nextRoom.contacts)
    : []
})

watch([() => props.event, currentUser], () => {
  if (props.event?.attendees) {
    participants.value = buildParticipantsFromAttendees(props.event.attendees)
  }
})

watch(allContacts, () => {
  if (!participants.value.length) {
    return
  }

  participants.value = dedupeParticipantIds(
    participants.value.map((id) => {
      if (!isEmail(id)) {
        return id
      }
      return findContactByEmail(id)?.guid ?? id
    }),
  )
})

</script>


<style lang="scss" scoped>
.shadow-schedule {
  box-shadow: 0px 0px 1px 0px rgba(12, 34, 69, 0.31), 0px 8px 12px 0px rgba(12, 34, 69, 0.15) ;
}
</style>
